// src/components/common/Navbar.tsx
import { Link, useNavigate } from "react-router-dom";
import { Menu, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { SearchBar } from "./SearchBar";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { APP_CONFIG } from "../../config/constants";


// Ícone de Carrinho
const CartIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
);

export function Navbar() {
    const { cartItems } = useCart();
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const itemCount = cartItems.length;
    const isAuthenticated = !!user;

    const handleLogout = () => {
        logout();
        navigate('/');
    };

    return (
        <header className="bg-white/95 backdrop-blur-lg sticky top-0 z-50 border-b border-gray-200 shadow-sm">
            <nav className="container mx-auto px-4 py-4">
                <div className="flex justify-between items-center">
                    {/* Logo e Navegação */}
                    <div className="flex items-center gap-8">
                        {/* Logo Premium */}
                        <Link to="/" className="flex items-center gap-2 group">
                            <div className="relative">
                                <div className="absolute inset-0 bg-brand-blue/20 rounded-xl blur-md group-hover:blur-lg transition-all"></div>
                                <div className="relative w-10 h-10 bg-gradient-to-br from-brand-blue to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                                    <svg className="w-6 h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                </div>
                            </div>
                            <span className="text-2xl font-extrabold bg-gradient-to-r from-brand-blue to-blue-600 bg-clip-text text-transparent">
                                {APP_CONFIG.storeName}
                            </span>
                        </Link>

                        {/* Links de Navegação */}
                        <div className="hidden md:flex items-center gap-1">
                            <Link
                                to="/plugins"
                                className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                            >
                                <span className="flex items-center gap-2">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                    </svg>
                                    Plugins
                                </span>
                            </Link>
                            {isAuthenticated && (
                                <Link
                                    to="/conta"
                                    className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-brand-blue hover:bg-blue-50 rounded-lg transition-all"
                                >
                                    <span className="flex items-center gap-2">
                                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                        </svg>
                                        Minha Conta
                                    </span>
                                </Link>
                            )}
                        </div>
                    </div>

                    {/* Lado Direito - Ações */}
                    <div className="flex items-center gap-3">
                        {/* SearchBar */}
                        <div className="hidden sm:block">
                            <SearchBar />
                        </div>

                        {/* Carrinho */}
                        <Link
                            to="/cart"
                            aria-label="Carrinho de compras"
                            className="relative p-2.5 rounded-xl hover:bg-gray-100 text-gray-700 transition-all group"
                        >
                            <CartIcon />
                            {itemCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-gradient-to-r from-red-500 to-red-600 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full font-bold shadow-lg animate-pulse">
                                    {itemCount}
                                </span>
                            )}
                        </Link>

                        {/* Menu de Usuário ou Login */}
                        {isAuthenticated ? (
                            <Menu as="div" className="relative">
                                <Menu.Button className="flex items-center gap-2 p-2 pr-3 rounded-xl hover:bg-gray-100 transition-all group">
                                    <div className="w-9 h-9 bg-gradient-to-br from-brand-blue to-blue-600 rounded-lg flex items-center justify-center shadow-md">
                                        <span className="text-white font-bold text-sm">{user.name.charAt(0).toUpperCase()}</span>
                                    </div>
                                    <svg className="w-4 h-4 text-gray-500 group-hover:text-gray-700 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                </Menu.Button>

                                <Transition
                                    as={Fragment}
                                    enter="transition ease-out duration-100"
                                    enterFrom="transform opacity-0 scale-95"
                                    enterTo="transform opacity-100 scale-100"
                                    leave="transition ease-in duration-75"
                                    leaveFrom="transform opacity-100 scale-100"
                                    leaveTo="transform opacity-0 scale-95"
                                >
                                    <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right bg-white rounded-2xl shadow-xl ring-1 ring-gray-200 focus:outline-none overflow-hidden">
                                        {/* Header do Menu */}
                                        <div className="px-4 py-3 bg-gradient-to-r from-brand-blue to-blue-600">
                                            <p className="text-xs text-blue-100 font-medium">Logado como</p>
                                            <p className="text-sm font-bold text-white truncate">{user.name}</p>
                                            {user.email && <p className="text-xs text-blue-100 truncate mt-0.5">{user.email}</p>}
                                        </div>

                                        <div className="py-1">
                                            <Menu.Item>
                                                {({ active }) => (
                                                    <Link
                                                        to="/conta"
                                                        className={`${active ? 'bg-blue-50 text-brand-blue' : 'text-gray-700'} flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors`}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                                        </svg>
                                                        Minha Conta
                                                    </Link>
                                                )}
                                            </Menu.Item>

                                            {user?.role === 'ADMIN' && (
                                                <Menu.Item>
                                                    {({ active }) => (
                                                        <Link
                                                            to="/admin/dashboard"
                                                            className={`${active ? 'bg-purple-50 text-purple-600' : 'text-gray-700'} flex items-center gap-3 px-4 py-2.5 text-sm font-medium transition-colors`}
                                                        >
                                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                                            </svg>
                                                            Painel Admin
                                                        </Link>
                                                    )}
                                                </Menu.Item>
                                            )}

                                            <div className="my-1 border-t border-gray-200"></div>

                                            <Menu.Item>
                                                {({ active }) => (
                                                    <button
                                                        onClick={handleLogout}
                                                        className={`${active ? 'bg-red-50 text-red-600' : 'text-red-500'} flex items-center gap-3 w-full px-4 py-2.5 text-sm font-medium transition-colors`}
                                                    >
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                        </svg>
                                                        Sair
                                                    </button>
                                                )}
                                            </Menu.Item>
                                        </div>
                                    </Menu.Items>
                                </Transition>
                            </Menu>
                        ) : (
                            <Link
                                to="/login"
                                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-bold text-white bg-gradient-to-r from-brand-blue to-blue-600 rounded-xl hover:from-blue-600 hover:to-blue-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                            >
                                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                                </svg>
                                Entrar
                            </Link>
                        )}
                    </div>
                </div>
            </nav>
        </header>
    );
}